import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, LogOut, HelpCircle, Settings as SettingsIcon, LifeBuoy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useMainNavItems } from '../hooks/useMainNavItems';
import NotificationBell from './NotificationBell';
import LanguageSwitcher from './LanguageSwitcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

const SideNav = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('header');
  // Shared source of truth with `TopBar` mobile drawer — see
  // `hooks/useMainNavItems.js`.
  const items = useMainNavItems();

  if (['/login', '/register'].includes(location.pathname)) return null;
  if (location.pathname.startsWith('/checkout/')) return null;
  if (!user) return null;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside className="fixed left-0 top-0 hidden h-screen w-[72px] border-r border-border bg-card md:flex md:flex-col xl:w-[245px]">
      <div className="flex h-20 items-center justify-between px-3 xl:px-6">
        <span className="hidden text-2xl xl:inline nymia-logo">
          Nymia
        </span>
        <span className="text-2xl xl:hidden nymia-logo">
          N
        </span>
        <NotificationBell />
      </div>
      <div className="flex items-center justify-center px-3 pb-1 xl:justify-start xl:px-6">
        <NavLink
          to="/help"
          aria-label={t('menu.help', 'Aiuto')}
          data-testid="sidenav-help-btn"
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors hover:bg-secondary ${
              isActive ? 'gold-text font-semibold' : 'text-muted-foreground'
            }`
          }
        >
          <HelpCircle className="h-5 w-5" strokeWidth={1.8} />
          <span className="hidden xl:inline">{t('menu.help', 'Aiuto')}</span>
        </NavLink>
      </div>
      <div className="hidden px-3 pb-3 xl:block">
        <LanguageSwitcher variant="full" align="start" className="w-full justify-center" testId="sidenav-language-switcher" />
      </div>
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-2 xl:px-3">
        {items.map(({ to, icon: Icon, key, end }) => {
          const label = t(`nav.${key}`);
          return (
            <NavLink
              key={key}
              to={to}
              end={end}
              data-testid={`sidenav-link-${key}`}
              className={({ isActive }) =>
                `flex items-center gap-4 rounded-lg p-3 transition-colors hover:bg-secondary ${
                  isActive ? 'gold-text font-semibold bg-secondary/60' : 'text-foreground font-normal'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className="h-6 w-6 shrink-0" strokeWidth={isActive ? 2.5 : 1.8} />
                  <span className="hidden text-base xl:inline">{label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className="px-2 pb-6 xl:px-3">
        <DropdownMenu>
          <DropdownMenuTrigger className="flex w-full items-center gap-4 rounded-lg p-3 text-foreground hover:bg-secondary hover:gold-text" data-testid="sidenav-more-btn">
            <Menu className="h-6 w-6 shrink-0" strokeWidth={1.8} />
            <span className="hidden text-base xl:inline">{t('nav.more')}</span>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-56">
            {/* Parity with the mobile drawer secondary items — desktop and
                mobile menus MUST expose the same buttons (owner request). */}
            <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="sidenav-more-settings">
              <SettingsIcon className="mr-2 h-4 w-4" /> {t('menu.settings')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => window.dispatchEvent(new CustomEvent('nymia:open-support-chat'))}
              data-testid="sidenav-more-support"
            >
              <LifeBuoy className="mr-2 h-4 w-4" /> {t('menu.support', 'Assistenza')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600" data-testid="sidenav-more-logout">
              <LogOut className="mr-2 h-4 w-4" /> {t('menu.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
};

export default SideNav;
